import React, { useState, useEffect } from 'react';
import { db, storage, auth } from '../firebase';
import { collection, addDoc, onSnapshot, query, where, doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-cards';
import { EffectCards } from 'swiper/modules';
import './Tinder.css';

const Tinder = () => {
    const [profiles, setProfiles] = useState([]);
    const [myPhotos, setMyPhotos] = useState([]);
    const [image, setImage] = useState(null);
    const [nickname, setNickname] = useState('');
    const [partnerNickname, setPartnerNickname] = useState('');
    const [swiper, setSwiper] = useState(null);
    const [activeTab, setActiveTab] = useState('swipe');

    useEffect(() => {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        let unsubscribeProfiles = () => {};
        let unsubscribeMyPhotos = () => {};

        const fetchUserData = async () => {
            const userDocRef = doc(db, 'users', currentUser.uid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
                const userData = userDoc.data();
                setNickname(userData.nickname);

                // Fetch partner's profiles for swiping
                if (userData.partnerId) {
                    const partnerDocRef = doc(db, 'users', userData.partnerId);
                    const partnerDoc = await getDoc(partnerDocRef);
                    if (partnerDoc.exists()) {
                        setPartnerNickname(partnerDoc.data().nickname);
                    }

                    const qProfiles = query(collection(db, 'tinderProfiles'), where('userId', '==', userData.partnerId));
                    unsubscribeProfiles = onSnapshot(qProfiles, (snapshot) => {
                        const profilesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        setProfiles(profilesData);
                    });
                }

                // Fetch current user's photos and their reactions
                const qMyPhotos = query(collection(db, 'tinderProfiles'), where('userId', '==', currentUser.uid));
                unsubscribeMyPhotos = onSnapshot(qMyPhotos, async (snapshot) => {
                    const photosData = await Promise.all(snapshot.docs.map(async (photoDoc) => { // Changed 'doc' to 'photoDoc'
                        const photo = { id: photoDoc.id, ...photoDoc.data() };
                        if (userData.partnerId) {
                            const reactionDocRef = doc(db, 'tinderProfiles', photoDoc.id, 'reactions', userData.partnerId);
                            const reactionDoc = await getDoc(reactionDocRef);
                            if (reactionDoc.exists()) {
                                photo.reaction = reactionDoc.data().reaction;
                            }
                        }
                        return photo;
                    }));
                    setMyPhotos(photosData);
                });
            }
        };

        fetchUserData();

        return () => {
            unsubscribeProfiles();
            unsubscribeMyPhotos();
        };
    }, []);

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            setImage(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (image) {
            const storageRef = ref(storage, `tinder-profiles/${auth.currentUser.uid}/${image.name}`);
            await uploadBytes(storageRef, image);
            const url = await getDownloadURL(storageRef);
            await addDoc(collection(db, 'tinderProfiles'), {
                userId: auth.currentUser.uid,
                nickname: nickname,
                imageUrl: url,
                createdAt: new Date()
            });
            setImage(null);
            document.getElementById('file-upload').value = null;
        }
    };

    const handleReaction = async (reactionType) => {
        if (!swiper || profiles.length === 0) return;

        const currentProfileIndex = swiper.activeIndex;
        const profile = profiles[currentProfileIndex];
        const currentUser = auth.currentUser;

        if (profile && currentUser) {
            const reactionRef = doc(db, 'tinderProfiles', profile.id, 'reactions', currentUser.uid);
            await setDoc(reactionRef, {
                reaction: reactionType,
                reactedAt: new Date(),
                reactorNickname: nickname
            });
        }

        if (reactionType === 'undo') {
            swiper.slidePrev();
        } else {
            swiper.slideNext();
        }
    };

    const renderReactionIcon = (reaction) => {
        switch (reaction) {
            case 'yes': return '❤️';
            case 'no': return '❌';
            case 'super-like': return '⭐';
            default: return null;
        }
    };

    return (
        <div className="tinder-container">
            <div className="tinder-tabs">
                <button onClick={() => setActiveTab('swipe')} className={activeTab === 'swipe' ? 'active' : ''}>Swipe</button>
                <button onClick={() => setActiveTab('my-photos')} className={activeTab === 'my-photos' ? 'active' : ''}>My Photos</button>
            </div>

            {activeTab === 'swipe' && (
                <div className="swipe-view">
                    <Swiper
                        effect={'cards'}
                        grabCursor={true}
                        modules={[EffectCards]}
                        className="mySwiper"
                        onSwiper={setSwiper}
                    >
                        {profiles.length > 0 ? profiles.map(profile => (
                            <SwiperSlide key={profile.id}>
                                <div style={{ backgroundImage: `url(${profile.imageUrl})` }} className="card">
                                    <h3>{partnerNickname}</h3>
                                    <div className="action-buttons">
                                        <button onClick={() => handleReaction('no')}>❌</button>
                                        <button onClick={() => handleReaction('undo')}>⏪</button>
                                        <button onClick={() => handleReaction('super-like')}>⭐</button>
                                        <button onClick={() => handleReaction('yes')}>❤️</button>
                                    </div>
                                </div>
                            </SwiperSlide>
                        )) : <SwiperSlide><div className='card'><h3>No profiles yet!</h3></div></SwiperSlide>}
                    </Swiper>
                </div>
            )}

            {activeTab === 'my-photos' && (
                <>
                    <div className="upload-section">
                        <label htmlFor="file-upload" className="custom-file-upload">
                            Choose File
                        </label>
                        <input id="file-upload" type="file" onChange={handleImageChange} />
                        {image && <span className="file-name">{image.name}</span>}
                        <button onClick={handleUpload} disabled={!image}>Upload</button>
                    </div>
                    <div className="my-photos-grid">
                        {myPhotos.map(photo => (
                            <div key={photo.id} className="my-photo-card" style={{ backgroundImage: `url(${photo.imageUrl})` }}>
                                {photo.reaction && <div className="reaction-icon">{renderReactionIcon(photo.reaction)}</div>}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default Tinder;
